// SNIGJ - Prisons & Cachots List Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, MapPin, Users, Search, Filter, Eye, 
  Shield, Lock, UserCheck, Baby, AlertTriangle,
  Phone, Mail, ChevronRight, BarChart3, ClipboardCheck,
  History, CheckCircle, XCircle, Clock, Edit, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import PrisonerRollCall from '../components/PrisonerRollCall';

// DRC Provinces
const DRC_PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

// Crime Types for filtering
const CRIME_TYPES = [
  { value: 'VOL', label: 'Vol' },
  { value: 'VIOL', label: 'Viol' },
  { value: 'MEURTRE', label: 'Meurtre/Assassinat' },
  { value: 'DETOURNEMENT', label: 'Détournement de fonds' },
  { value: 'ESCROQUERIE', label: 'Escroquerie' },
  { value: 'TRAFIC_DROGUE', label: 'Trafic de drogue' },
  { value: 'VIOLENCE', label: 'Violence/Coups et blessures' },
  { value: 'CORRUPTION', label: 'Corruption' },
  { value: 'TERRORISME', label: 'Terrorisme' },
  { value: 'REBELLION', label: 'Rébellion' },
  { value: 'AUTRE', label: 'Autre' }
];

const PrisonsListPage = () => {
  const { language, user } = useAuth();
  const navigate = useNavigate();
  const [establishments, setEstablishments] = useState([]);
  const [prisoners, setPrisoners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProvince, setFilterProvince] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRollCallModal, setShowRollCallModal] = useState(false);
  const [rollCallHistory, setRollCallHistory] = useState([]);
  const [detailTab, setDetailTab] = useState('inmates');
  
  // Filters for inmates detail view
  const [inmateSexFilter, setInmateSexFilter] = useState('all');
  const [inmateAgeFilter, setInmateAgeFilter] = useState('all');
  const [inmateCrimeFilter, setInmateCrimeFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load structures that are prisons or cachots
      const [structuresRes, prisonersRes] = await Promise.all([
        api.get('/structures'),
        api.get('/prisoners')
      ]);

      const allStructures = structuresRes.data || [];
      const allPrisoners = prisonersRes.data?.prisoners || prisonersRes.data || [];

      // Filter to get only prisons and cachots
      const prisonTypes = ['PRISON_CENTRALE', 'PRISON_MAKALA', 'CACHOT_COMMISSARIAT', 'CACHOT_TRIBUNAL_PAIX', 'CACHOT_PARQUET_GI', 'CACHOT_COUR_APPEL'];
      const prisonStructures = allStructures.filter(s => 
        prisonTypes.includes(s.structure_type) || s.has_cachot
      );

      // Add prisoner counts to each establishment
      const establishmentsWithCounts = prisonStructures.map(est => {
        const estPrisoners = allPrisoners.filter(p => p.prison_id === est.structure_id);
        return {
          ...est,
          total_inmates: estPrisoners.length,
          male_inmates: estPrisoners.filter(p => p.gender === 'M').length,
          female_inmates: estPrisoners.filter(p => p.gender === 'F').length,
          preventive: estPrisoners.filter(p => p.status === 'PREVENTIVE').length,
          sentenced: estPrisoners.filter(p => p.status === 'SENTENCED').length,
          minors: estPrisoners.filter(p => {
            if (!p.date_of_birth) return false;
            const age = new Date().getFullYear() - new Date(p.date_of_birth).getFullYear();
            return age < 18;
          }).length,
          inmates: estPrisoners
        };
      });

      setEstablishments(establishmentsWithCounts);
      setPrisoners(allPrisoners);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load roll call history for selected establishment
  const loadRollCallHistory = async (prisonId) => {
    try {
      const response = await api.get(`/roll-call/prison/${prisonId}`);
      setRollCallHistory(response.data?.roll_calls || []);
    } catch (error) {
      console.error('Failed to load roll call history:', error);
      setRollCallHistory([]);
    }
  };

  // Open establishment detail with roll call history
  const openEstablishmentDetail = async (est) => {
    setSelectedEstablishment(est);
    setInmateSexFilter('all');
    setInmateAgeFilter('all');
    setInmateCrimeFilter('all');
    setDetailTab('inmates');
    setShowDetailModal(true);
    loadRollCallHistory(est.structure_id);
  };

  // Start roll call for establishment
  const startRollCall = (est) => {
    setSelectedEstablishment(est);
    setShowDetailModal(false);
    setShowRollCallModal(true);
  };

  // Handle roll call completion
  const handleRollCallComplete = (result) => {
    toast.success(
      language === 'fr' 
        ? `Appel terminé: ${result.present} présents, ${result.absent} absents`
        : `Roll call complete: ${result.present} present, ${result.absent} absent`
    );
    // Reload roll call history if detail modal is still accessible
    if (selectedEstablishment) {
      loadRollCallHistory(selectedEstablishment.structure_id);
    }
  };

  // Calculate totals
  const totals = {
    establishments: establishments.length,
    prisons: establishments.filter(e => e.structure_type?.includes('PRISON')).length,
    cachots: establishments.filter(e => e.structure_type?.includes('CACHOT') || e.has_cachot).length,
    totalInmates: establishments.reduce((sum, e) => sum + (e.total_inmates || 0), 0),
    maleInmates: establishments.reduce((sum, e) => sum + (e.male_inmates || 0), 0),
    femaleInmates: establishments.reduce((sum, e) => sum + (e.female_inmates || 0), 0),
    preventive: establishments.reduce((sum, e) => sum + (e.preventive || 0), 0),
    sentenced: establishments.reduce((sum, e) => sum + (e.sentenced || 0), 0),
    minors: establishments.reduce((sum, e) => sum + (e.minors || 0), 0)
  };

  // Filter establishments
  const filteredEstablishments = establishments.filter(est => {
    const matchesSearch = !searchQuery || 
      est.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      est.province?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvince = filterProvince === 'all' || est.province === filterProvince;
    const matchesType = filterType === 'all' || 
      (filterType === 'prison' && est.structure_type?.includes('PRISON')) ||
      (filterType === 'cachot' && (est.structure_type?.includes('CACHOT') || est.has_cachot));
    return matchesSearch && matchesProvince && matchesType;
  });

  // Filter inmates in detail view
  const getFilteredInmates = () => {
    if (!selectedEstablishment?.inmates) return [];
    return selectedEstablishment.inmates.filter(inmate => {
      const matchesSex = inmateSexFilter === 'all' || inmate.gender === inmateSexFilter;
      const matchesCrime = inmateCrimeFilter === 'all' || 
        (inmate.crime_types && inmate.crime_types.includes(inmateCrimeFilter));
      
      let matchesAge = true;
      if (inmateAgeFilter !== 'all' && inmate.date_of_birth) {
        const age = new Date().getFullYear() - new Date(inmate.date_of_birth).getFullYear();
        if (inmateAgeFilter === 'minor') matchesAge = age < 18;
        else if (inmateAgeFilter === 'adult') matchesAge = age >= 18 && age < 60;
        else if (inmateAgeFilter === 'elderly') matchesAge = age >= 60;
      }
      
      return matchesSex && matchesCrime && matchesAge;
    });
  };

  const getEstablishmentTypeLabel = (type, has_cachot) => {
    if (type?.includes('PRISON')) return language === 'fr' ? 'Prison' : 'Prison';
    if (type?.includes('CACHOT') || has_cachot) return 'Cachot';
    return type;
  };

  const getEstablishmentIcon = (type, has_cachot) => {
    if (type?.includes('PRISON')) return <Building2 className="w-5 h-5 text-orange-600" />;
    return <Lock className="w-5 h-5 text-red-600" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="prisons-list-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-7 h-7 text-primary" />
            {language === 'fr' ? 'Établissements Pénitentiaires' : 'Penitentiary Establishments'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Liste des prisons et cachots avec détails des détenus'
              : 'List of prisons and detention centers with inmate details'}
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border-orange-200 dark:border-orange-800">
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-3xl font-bold text-orange-700 dark:text-orange-300">{totals.prisons}</p>
              <p className="text-xs text-orange-600 dark:text-orange-400">{language === 'fr' ? 'Prisons' : 'Prisons'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800">
            <CardContent className="p-4 text-center">
              <Lock className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-3xl font-bold text-red-700 dark:text-red-300">{totals.cachots}</p>
              <p className="text-xs text-red-600 dark:text-red-400">{language === 'fr' ? 'Cachots' : 'Detention Centers'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{totals.totalInmates}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">{language === 'fr' ? 'Total Détenus' : 'Total Inmates'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <UserCheck className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-3xl font-bold">{totals.maleInmates}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Hommes' : 'Male'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto text-pink-600 mb-2" />
              <p className="text-3xl font-bold">{totals.femaleInmates}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Femmes' : 'Female'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950 dark:to-yellow-900 border-yellow-200 dark:border-yellow-800">
            <CardContent className="p-4 text-center">
              <Baby className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
              <p className="text-3xl font-bold text-yellow-700 dark:text-yellow-300">{totals.minors}</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">{language === 'fr' ? 'Mineurs' : 'Minors'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Status Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{language === 'fr' ? 'Répartition par statut' : 'Status Distribution'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{language === 'fr' ? 'Préventive' : 'Preventive'}</span>
                  <span className="font-medium">{totals.preventive}</span>
                </div>
                <Progress value={totals.totalInmates ? (totals.preventive / totals.totalInmates) * 100 : 0} className="h-2 bg-orange-100" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{language === 'fr' ? 'Condamnés' : 'Sentenced'}</span>
                  <span className="font-medium">{totals.sentenced}</span>
                </div>
                <Progress value={totals.totalInmates ? (totals.sentenced / totals.totalInmates) * 100 : 0} className="h-2 bg-blue-100" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher un établissement...' : 'Search establishment...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-prisons"
                />
              </div>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="prison">{language === 'fr' ? 'Prisons' : 'Prisons'}</SelectItem>
                  <SelectItem value="cachot">{language === 'fr' ? 'Cachots' : 'Detention'}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterProvince} onValueChange={setFilterProvince}>
                <SelectTrigger className="w-[180px]">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes les provinces' : 'All Provinces'}</SelectItem>
                  {DRC_PROVINCES.map(province => (
                    <SelectItem key={province} value={province}>{province}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Badge variant="outline" className="text-muted-foreground">
                {filteredEstablishments.length} {language === 'fr' ? 'établissement(s)' : 'establishment(s)'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Establishments List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredEstablishments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">
                {language === 'fr' ? 'Aucun établissement trouvé' : 'No establishments found'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Les prisons et cachots doivent être créés dans Administration > Structures'
                  : 'Prisons and detention centers must be created in Administration > Structures'}
              </p>
              <Button onClick={() => navigate('/admin/structures')}>
                {language === 'fr' ? 'Gérer les structures' : 'Manage Structures'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEstablishments.map((est) => (
              <Card 
                key={est.structure_id} 
                className="hover:shadow-lg transition-all cursor-pointer border-l-4"
                style={{ 
                  borderLeftColor: est.structure_type?.includes('PRISON') ? '#ea580c' : '#dc2626' 
                }}
                onClick={() => openEstablishmentDetail(est)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getEstablishmentIcon(est.structure_type, est.has_cachot)}
                      <div>
                        <CardTitle className="text-base">{est.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={
                            est.structure_type?.includes('PRISON') 
                              ? 'text-orange-600 border-orange-300 bg-orange-50'
                              : 'text-red-600 border-red-300 bg-red-50'
                          }>
                            {getEstablishmentTypeLabel(est.structure_type, est.has_cachot)}
                          </Badge>
                          {est.justice_type === 'MILITAIRE' && (
                            <Badge variant="outline" className="text-purple-600 border-purple-300 bg-purple-50">
                              ⚔️ Militaire
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/prisons/${est.structure_id}/edit`);
                        }}
                        title={language === 'fr' ? 'Éditer' : 'Edit'}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Location */}
                    {est.province && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{est.province}</span>
                        {est.address && <span className="truncate">- {est.address}</span>}
                      </div>
                    )}

                    {/* Inmate Stats */}
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{est.total_inmates || 0}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">{language === 'fr' ? 'Détenus' : 'Inmates'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-green-600">{est.male_inmates || 0}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">H</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-pink-600">{est.female_inmates || 0}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">F</p>
                      </div>
                    </div>

                    {/* Status bar */}
                    {est.total_inmates > 0 && (
                      <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-muted">
                        <div 
                          className="bg-orange-500 transition-all" 
                          style={{ width: `${(est.preventive / est.total_inmates) * 100}%` }}
                          title={`Préventive: ${est.preventive}`}
                        />
                        <div 
                          className="bg-blue-500 transition-all" 
                          style={{ width: `${(est.sentenced / est.total_inmates) * 100}%` }}
                          title={`Condamnés: ${est.sentenced}`}
                        />
                      </div>
                    )}

                    {/* Minors warning */}
                    {est.minors > 0 && (
                      <div className="flex items-center gap-2 text-yellow-600 text-sm bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                        <AlertTriangle className="w-4 h-4" />
                        <span>{est.minors} {language === 'fr' ? 'mineur(s)' : 'minor(s)'}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedEstablishment && getEstablishmentIcon(selectedEstablishment.structure_type, selectedEstablishment.has_cachot)}
                {selectedEstablishment?.name}
              </DialogTitle>
            </DialogHeader>

            {selectedEstablishment && (
              <div className="space-y-6">
                {/* Establishment Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{selectedEstablishment.total_inmates}</p>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total Détenus' : 'Total Inmates'}</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{selectedEstablishment.male_inmates}</p>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Hommes' : 'Male'}</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">{selectedEstablishment.female_inmates}</p>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Femmes' : 'Female'}</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{selectedEstablishment.minors}</p>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Mineurs' : 'Minors'}</p>
                  </div>
                </div>

                {/* Roll Call Button - Prominent */}
                <Button 
                  onClick={() => startRollCall(selectedEstablishment)}
                  className="w-full gap-2 h-12 text-lg bg-primary hover:bg-primary/90"
                  data-testid="start-roll-call-btn"
                >
                  <ClipboardCheck className="w-6 h-6" />
                  {language === 'fr' ? 'Démarrer l\'Appel des Prisonniers' : 'Start Prisoner Roll Call'}
                </Button>

                {/* Location Details */}
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedEstablishment.province && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span><strong>Province:</strong> {selectedEstablishment.province}</span>
                        </div>
                      )}
                      {selectedEstablishment.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span><strong>Adresse:</strong> {selectedEstablishment.address}</span>
                        </div>
                      )}
                      {selectedEstablishment.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedEstablishment.phone}</span>
                        </div>
                      )}
                      {selectedEstablishment.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedEstablishment.email}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs: Inmates / Roll Call History */}
                <Tabs value={detailTab} onValueChange={setDetailTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="inmates" className="gap-2">
                      <Users className="w-4 h-4" />
                      {language === 'fr' ? 'Liste des Détenus' : 'Inmates List'}
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-2">
                      <History className="w-4 h-4" />
                      {language === 'fr' ? 'Historique Appels' : 'Roll Call History'}
                    </TabsTrigger>
                  </TabsList>

                  {/* Inmates Tab */}
                  <TabsContent value="inmates" className="space-y-4 mt-4">
                    {/* Filters for Inmates */}
                    <div className="flex flex-wrap gap-3">
                      <Select value={inmateSexFilter} onValueChange={setInmateSexFilter}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Sexe" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                          <SelectItem value="M">{language === 'fr' ? 'Hommes' : 'Male'}</SelectItem>
                          <SelectItem value="F">{language === 'fr' ? 'Femmes' : 'Female'}</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={inmateAgeFilter} onValueChange={setInmateAgeFilter}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Âge" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{language === 'fr' ? 'Tous âges' : 'All Ages'}</SelectItem>
                          <SelectItem value="minor">{language === 'fr' ? 'Mineurs (<18)' : 'Minors (<18)'}</SelectItem>
                          <SelectItem value="adult">{language === 'fr' ? 'Adultes (18-60)' : 'Adults (18-60)'}</SelectItem>
                          <SelectItem value="elderly">{language === 'fr' ? 'Aînés (60+)' : 'Elderly (60+)'}</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={inmateCrimeFilter} onValueChange={setInmateCrimeFilter}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Type de crime" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{language === 'fr' ? 'Tous crimes' : 'All Crimes'}</SelectItem>
                          {CRIME_TYPES.map(crime => (
                            <SelectItem key={crime.value} value={crime.value}>{crime.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Badge variant="secondary">
                        {getFilteredInmates().length} {language === 'fr' ? 'détenu(s)' : 'inmate(s)'}
                      </Badge>
                    </div>

                    {/* Inmates List */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted p-3 font-medium text-sm grid grid-cols-5 gap-2">
                        <span>{language === 'fr' ? 'Nom' : 'Name'}</span>
                        <span>{language === 'fr' ? 'Sexe' : 'Gender'}</span>
                        <span>{language === 'fr' ? 'Statut' : 'Status'}</span>
                        <span>{language === 'fr' ? 'Crime' : 'Crime'}</span>
                        <span className="text-right">{language === 'fr' ? 'Actions' : 'Actions'}</span>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {getFilteredInmates().length === 0 ? (
                          <div className="p-8 text-center text-muted-foreground">
                            {language === 'fr' ? 'Aucun détenu trouvé' : 'No inmates found'}
                          </div>
                        ) : (
                          getFilteredInmates().map((inmate) => (
                            <div 
                              key={inmate.prisoner_id} 
                              className="p-3 border-b last:border-b-0 grid grid-cols-5 gap-2 items-center hover:bg-muted/50 text-sm"
                            >
                              <span className="font-medium">
                                {inmate.last_name} {inmate.first_name}
                              </span>
                              <span>
                                <Badge variant="outline" className={inmate.gender === 'M' ? 'text-blue-600' : 'text-pink-600'}>
                                  {inmate.gender === 'M' ? '♂ H' : '♀ F'}
                                </Badge>
                              </span>
                              <span>
                                <Badge variant={inmate.status === 'PREVENTIVE' ? 'warning' : 'default'}>
                                  {inmate.status === 'PREVENTIVE' ? 'Préventive' : 'Condamné'}
                                </Badge>
                              </span>
                              <span className="truncate text-muted-foreground">
                                {inmate.crime_types?.length > 0 
                                  ? CRIME_TYPES.find(c => c.value === inmate.crime_types[0])?.label || inmate.crime_types[0]
                                  : '-'}
                              </span>
                              <span className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/prisoners/${inmate.prisoner_id}`);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Roll Call History Tab */}
                  <TabsContent value="history" className="mt-4">
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted p-3 font-medium text-sm grid grid-cols-5 gap-2">
                        <span>{language === 'fr' ? 'Date' : 'Date'}</span>
                        <span>{language === 'fr' ? 'Type' : 'Type'}</span>
                        <span>{language === 'fr' ? 'Présents' : 'Present'}</span>
                        <span>{language === 'fr' ? 'Absents' : 'Absent'}</span>
                        <span>{language === 'fr' ? 'Statut' : 'Status'}</span>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {rollCallHistory.length === 0 ? (
                          <div className="p-8 text-center text-muted-foreground">
                            {language === 'fr' ? 'Aucun appel enregistré' : 'No roll calls recorded'}
                          </div>
                        ) : (
                          rollCallHistory.map((rc) => (
                            <div 
                              key={rc.roll_call_id} 
                              className="p-3 border-b last:border-b-0 grid grid-cols-5 gap-2 items-center hover:bg-muted/50 text-sm"
                            >
                              <span className="font-medium">
                                {new Date(rc.started_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              <span>
                                <Badge variant="outline">{rc.roll_call_type}</Badge>
                              </span>
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-green-600 font-medium">{rc.present}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <XCircle className="w-4 h-4 text-red-600" />
                                <span className="text-red-600 font-medium">{rc.absent}</span>
                              </span>
                              <span>
                                <Badge variant={rc.status === 'COMPLETED' ? 'default' : 'warning'}>
                                  {rc.status === 'COMPLETED' 
                                    ? (language === 'fr' ? 'Terminé' : 'Completed')
                                    : (language === 'fr' ? 'En cours' : 'In Progress')}
                                </Badge>
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                    {language === 'fr' ? 'Fermer' : 'Close'}
                  </Button>
                  <Button onClick={() => navigate('/prisoners?prison_id=' + selectedEstablishment.structure_id)}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Voir tous les détenus' : 'View All Inmates'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Roll Call Modal */}
        <PrisonerRollCall
          open={showRollCallModal}
          onClose={() => setShowRollCallModal(false)}
          prison={selectedEstablishment}
          onComplete={handleRollCallComplete}
        />
      </div>
    </DashboardLayout>
  );
};

export default PrisonsListPage;
